import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { registerPortalTokenSource } from '../lib/portal/customerClient';

export interface PortalContact {
  id: string;
  name: string;
  email: string;
}

export interface PortalTenant {
  id: string;
  name: string;
  slug: string;
}

/**
 * The session payload the server returns from login/verify/reset. The tenant is
 * resolved server-side from the credential and kept only for display — the
 * single-route portal no longer uses it for routing.
 */
export interface PortalSession {
  token: string;
  expiresAt: string;
  contact: PortalContact;
  tenant: PortalTenant;
}

interface PortalAuthState {
  token: string | null;
  expiresAt: string | null;
  contact: PortalContact | null;
  tenant: PortalTenant | null;
  setSession: (session: PortalSession) => void;
  clear: () => void;
}

/**
 * Client-portal customer session — a separate identity plane from the staff
 * `auth.store`. Persisted so a homeowner stays logged in across reloads; the
 * opaque session token is validated server-side on every request (and revoked
 * when the credential is disabled or the contact is removed). There is a single
 * portal route, so no per-tenant slug scoping is needed here.
 */
export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    (set) => ({
      token: null,
      expiresAt: null,
      contact: null,
      tenant: null,
      setSession: (session) =>
        set({
          token: session.token,
          expiresAt: session.expiresAt,
          contact: session.contact,
          tenant: session.tenant,
        }),
      clear: () => set({ token: null, expiresAt: null, contact: null, tenant: null }),
    }),
    {
      name: 'portal-auth',
      // v2 drops the slug-scoped shape (single-route portal). Any earlier
      // persisted session is cleared so the customer re-authenticates cleanly.
      version: 2,
      migrate: () =>
        ({ token: null, expiresAt: null, contact: null, tenant: null }) as PortalAuthState,
    },
  ),
);

// The customer API client reads the live token from this store on every request.
registerPortalTokenSource(() => usePortalAuthStore.getState().token);

/** True when a non-expired session token is present. */
export function hasLivePortalSession(): boolean {
  const { token, expiresAt } = usePortalAuthStore.getState();
  if (!token) return false;
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() > Date.now();
}
